import React from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';

interface DaySchedule {
  open: string;
  close: string;
  closed: boolean;
}

interface Schedule {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
  emergency_available: boolean;
}

interface ScheduleInputProps {
  schedule: Schedule;
  onChange: (schedule: Schedule) => void;
}

export default function ScheduleInput({ schedule, onChange }: ScheduleInputProps) {
  const { t } = useTranslation();

  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ] as const;

  const handleDayChange = (
    day: keyof Omit<Schedule, 'emergency_available'>,
    field: keyof DaySchedule,
    value: string | boolean
  ) => {
    onChange({
      ...schedule,
      [day]: {
        ...schedule[day],
        [field]: value,
      },
    });
  };

  return (
    <Card className="p-4 space-y-4">
      <div>
        <h3 className="font-semibold mb-2">{t('services.registerProvider.schedule')}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {t('services.registerProvider.scheduleDescription')}
        </p>
      </div>

      <div className="space-y-3">
        {days.map((day) => (
          <div
            key={day}
            className="flex flex-col md:flex-row md:items-center gap-3 p-3 rounded-lg bg-muted/30"
          >
            <Label className="font-medium capitalize md:w-28 md:flex-shrink-0">
              {t(`services.registerProvider.${day}`)}
            </Label>

            <div className="flex items-center gap-2 md:w-32 md:flex-shrink-0">
              <Switch
                checked={schedule[day].closed}
                onCheckedChange={(checked) =>
                  handleDayChange(day, 'closed', checked)
                }
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {t('services.registerProvider.closed')}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Label className="text-sm whitespace-nowrap flex-shrink-0">
                {t('services.registerProvider.openTime')}:
              </Label>
              <Input
                type="time"
                value={schedule[day].open}
                onChange={(e) => handleDayChange(day, 'open', e.target.value)}
                disabled={schedule[day].closed}
                className="w-full min-w-[100px]"
              />
            </div>

            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Label className="text-sm whitespace-nowrap flex-shrink-0">
                {t('services.registerProvider.closeTime')}:
              </Label>
              <Input
                type="time"
                value={schedule[day].close}
                onChange={(e) => handleDayChange(day, 'close', e.target.value)}
                disabled={schedule[day].closed}
                className="w-full min-w-[100px]"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
        <Switch
          checked={schedule.emergency_available}
          onCheckedChange={(checked) =>
            onChange({ ...schedule, emergency_available: checked })
          }
          className="mt-1"
        />
        <div>
          <Label className="font-medium text-primary">
            {t('services.registerProvider.emergencyAvailable')}
          </Label>
          <p className="text-sm text-muted-foreground mt-1">
            {t('services.registerProvider.emergencyDescription')}
          </p>
        </div>
      </div>
    </Card>
  );
}
